<!--
 * @Description: 个体发票-申请开票（票税修改 v2）
 *               isConfirm 控制编辑/只读 + 开票备注显示
 * @Date: 2025-09-16 / 重构: 2026-06-03
-->
<template>
  <BaseDialog
    width="950px"
    class="form-dialog form-dialog--label120 form-dialog-invoice-custom"
    :title="isConfirm ? '申请开票 - 确认' : '申请开票'"
    :model-value="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
  >
    <BaseTextContent v-if="show">
      <!-- 开票信息（只读） -->
      <InvoiceOpenDialogInfo :baseInfo="baseInfo" />

      <InvoiceApplyIndividualDialogContent
        ref="contentRef"
        :disabled="isConfirm"
        v-model="formData"
      />

      <!-- 开票备注（仅确认步骤显示） -->
      <div class="modal-section" v-if="isConfirm">
        <div class="modal-section-title">开票备注</div>
        <BaseInput
          type="textarea"
          :maxlength="120"
          :rows="2"
          show-word-limit
          placeholder="请输入"
          v-model="formData.demand"
        />
      </div>

      <!-- 开票数据模块（类目分组容器） -->
      <IndividualCategoryGroup
        ref="categoryRef"
        v-model:categoryGroups="categoryGroups"
        :formData="formData"
        :isConfirm="isConfirm"
        :disabled="isConfirm"
      />
    </BaseTextContent>

    <template #footer>
      <BaseButton type="default" @click="onCancel">
        {{ isConfirm ? '上一步' : '取消' }}
      </BaseButton>
      <BaseButton @click="onConfirm">
        {{ isConfirm ? '确认提交' : '下一步' }}
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import {
  BaseDialog,
  BaseTextContent,
  BaseButton,
  BaseInput,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';
import {
  individualInvoiceApplySubmit,
  basicInformation,
  invoiceDetail
} from '@V/invoice/src/data-source/apis';
import type {
  IndividualInvoiceApplySubmitReqDTO,
  MakeOutAnInvoiceParams,
  CategoryGroups,
  BasicInformationData
} from '@V/invoice/src/data-source/types';
import InvoiceApplyIndividualDialogContent from './invoice-apply-individual-dialog-content.vue';
import IndividualCategoryGroup from './individual-category-group.vue';
import InvoiceOpenDialogInfo from '../invoice-open-dialog/invoice-open-dialog-info.vue';
import { useCloned } from '@vueuse/core';

type InvoiceApplyFormData = Pick<
  IndividualInvoiceApplySubmitReqDTO,
  'invoiceType' | 'theSellerId' | 'payPassagewayCode'
> & {
  payType: string;
  demand: string;
  individualId: number | null;
  individualName: string;
  receiveAt: string[];
  stateType: number;
} & Pick<
    MakeOutAnInvoiceParams,
    'itemId' | 'openOrderIds' | 'addressId' | 'auditSign'
  >;

const props = defineProps<{
  remove?: () => void;
  id?: number;
}>();

const emits = defineEmits<{
  (event: 'success'): void;
}>();

const show = ref(false); // 解决初始数据和获取数据后初始的部分组件延迟问题
const isConfirm = ref(false);
const contentRef = ref();
const categoryRef = ref();
const baseInfo = ref<BasicInformationData['baseInfo']>({
  entpName: '',
  entpCertNo: '',
  phone: '',
  bankName: '',
  openBankNumber: '',
  companyBusinessAddress: '',
  demand: ''
});
const addressList = ref<BasicInformationData['addressList']>([]);
const categoryGroups = ref<CategoryGroups>([
  { itemId: null, itemName: '', settlementList: [] }
]);
/* 所有表单内的数据，融合了详情接口，剔除开票数据字段 */
const formData = ref<InvoiceApplyFormData>({
  theSellerId: null,
  invoiceType: null,
  payPassagewayCode: null,
  payType: '',
  individualId: null,
  individualName: '',
  receiveAt: [],
  demand: '',
  stateType: 7,
  itemId: null,
  openOrderIds: [],
  addressId: null,
  auditSign: 1
});

const onConfirm = async () => {
  if (!isConfirm.value) {
    await categoryRef.value?.validate();
    await contentRef.value?.validate();
    isConfirm.value = true;
    return;
  }
  const [address] = addressList.value;
  const params = {
    ...baseInfo.value,
    receiptFullName: address.name,
    receiptPhone: address.phone,
    receiptAddress: address.mailAddress,
    email: address.email,
    receiptId: address.id,
    theSellerId: formData.value.theSellerId,
    invoiceType: formData.value.invoiceType,
    individualId: formData.value.individualId,
    individualName: formData.value.individualName,
    payPassagewayCode: formData.value.payPassagewayCode,
    demand: formData.value.demand,
    payType: formData.value.payType,
    receiveAt: formData.value.receiveAt,
    itemList: categoryGroups.value,
    ...(formData.value.receiveAt.length && {
      createAtStart: formData.value.receiveAt[0],
      createAtEnd: formData.value.receiveAt[1]
    }),
    ...(props.id && { invoiceApplyId: props.id })
  };

  await XrkMessageBox.confirm(
    '提交后将生效当前操作，无法撤回，请确认是否继续。',
    '确认开票',
    {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    }
  );
  await individualInvoiceApplySubmit(params);
  XrkMessage.success('已提交开票申请');
  emits('success');
  props.remove?.();
};

const onCancel = () => {
  if (isConfirm.value) {
    isConfirm.value = false;
    return;
  }
  props.remove?.();
};

onMounted(async () => {
  const { data } = await basicInformation();
  baseInfo.value = data.baseInfo;
  addressList.value = data.addressList || [];

  if (props.id) {
    const { data: detail } = await invoiceDetail({ id: props.id });
    const d = detail as any;
    const info = d?.info || d || {};

    formData.value = {
      ...formData.value,
      ...info,
      theSellerId: info.theSellerId || info.individualId
    };

    const _itemList = d?.itemSettlementGroupList || info.itemDetailList;
    const { cloned } = useCloned(_itemList);
    categoryGroups.value = cloned.value;
  }
  show.value = true;
});
</script>

<style lang="scss" scoped>
.modal-section {
  margin-bottom: 20px;
}

.modal-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
